// Tile queries shared by the server simulation and client prediction.

import { T_BLOCK, T_FLOOR, T_WALL, ZoneMap } from './types';

export function tileAt(map: ZoneMap, x: number, y: number): number {
  const tx = Math.floor(x);
  const ty = Math.floor(y);
  if (tx < 0 || ty < 0 || tx >= map.w || ty >= map.h) return T_WALL;
  return map.tiles[ty * map.w + tx];
}

export function blocksWalk(t: number, fly = false): boolean {
  if (fly) return t === T_WALL || t === T_BLOCK;
  return t !== T_FLOOR;
}

export function blocksShot(t: number): boolean {
  return t === T_WALL || t === T_BLOCK;
}

function boxBlocked(map: ZoneMap, x: number, y: number, r: number, fly: boolean): boolean {
  const x0 = Math.floor(x - r);
  const x1 = Math.floor(x + r);
  const y0 = Math.floor(y - r);
  const y1 = Math.floor(y + r);
  for (let ty = y0; ty <= y1; ty++) {
    for (let tx = x0; tx <= x1; tx++) {
      if (tx < 0 || ty < 0 || tx >= map.w || ty >= map.h) return true;
      if (blocksWalk(map.tiles[ty * map.w + tx], fly)) return true;
    }
  }
  return false;
}

/**
 * Moves a box of half-size r by (dx, dy), sliding along walls. Deterministic: both
 * client prediction and the server call this with identical inputs.
 */
export function moveWithCollision(map: ZoneMap, x: number, y: number, r: number, dx: number, dy: number, fly = false): { x: number; y: number } {
  const dist = Math.hypot(dx, dy);
  const steps = Math.max(1, Math.ceil(dist / 0.25));
  const sx = dx / steps;
  const sy = dy / steps;
  let cx = x;
  let cy = y;
  for (let i = 0; i < steps; i++) {
    if (sx !== 0 && !boxBlocked(map, cx + sx, cy, r, fly)) cx += sx;
    if (sy !== 0 && !boxBlocked(map, cx, cy + sy, r, fly)) cy += sy;
  }
  return { x: cx, y: cy };
}

export function hasLineOfSight(map: ZoneMap, x0: number, y0: number, x1: number, y1: number): boolean {
  const dx = x1 - x0;
  const dy = y1 - y0;
  const dist = Math.hypot(dx, dy);
  if (dist < 0.01) return true;
  const steps = Math.ceil(dist / 0.34);
  const sx = dx / steps;
  const sy = dy / steps;
  let x = x0;
  let y = y0;
  for (let i = 0; i <= steps; i++) {
    if (blocksShot(tileAt(map, x, y))) return false;
    x += sx;
    y += sy;
  }
  return true;
}

/** Furthest walkable point on a straight line from origin toward target (used for blink/leap). */
export function furthestWalkable(map: ZoneMap, x0: number, y0: number, x1: number, y1: number, r: number): { x: number; y: number } {
  const dx = x1 - x0;
  const dy = y1 - y0;
  const dist = Math.hypot(dx, dy);
  const steps = Math.max(1, Math.ceil(dist / 0.2));
  for (let i = steps; i >= 1; i--) {
    const nx = x0 + (dx * i) / steps;
    const ny = y0 + (dy * i) / steps;
    if (!boxBlocked(map, nx, ny, r, false)) return { x: nx, y: ny };
  }
  return { x: x0, y: y0 };
}

export function nearestWalkable(map: ZoneMap, x: number, y: number, maxR = 8): { x: number; y: number } {
  const cx = Math.floor(x);
  const cy = Math.floor(y);
  if (tileAt(map, cx, cy) === T_FLOOR) return { x, y };
  for (let r = 1; r <= maxR; r++) {
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        if (Math.abs(dx) !== r && Math.abs(dy) !== r) continue;
        if (tileAt(map, cx + dx, cy + dy) === T_FLOOR) return { x: cx + dx + 0.5, y: cy + dy + 0.5 };
      }
    }
  }
  return { x, y };
}

/**
 * A floor tile at least minDist from a point, and reachable from it on foot, so a wall never
 * separates the two. Stays clear of anything in `avoid`.
 */
export function spotBeside(map: ZoneMap, x: number, y: number, minDist: number, avoid: { x: number; y: number }[] = []): { x: number; y: number } {
  const start = nearestWalkable(map, x, y);
  const seen = new Set<number>();
  const queue = [[Math.floor(start.x), Math.floor(start.y)]];
  seen.add(queue[0][1] * map.w + queue[0][0]);
  for (let i = 0; i < queue.length; i++) {
    const [tx, ty] = queue[i];
    const cx = tx + 0.5;
    const cy = ty + 0.5;
    if (Math.hypot(cx - x, cy - y) >= minDist && avoid.every((a) => Math.hypot(cx - a.x, cy - a.y) >= 2)) return { x: cx, y: cy };
    for (const [dx, dy] of [[0, 1], [1, 0], [-1, 0], [0, -1]]) {
      const nx = tx + dx;
      const ny = ty + dy;
      const key = ny * map.w + nx;
      if (seen.has(key) || tileAt(map, nx, ny) !== T_FLOOR) continue;
      seen.add(key);
      queue.push([nx, ny]);
    }
  }
  return start;
}

/**
 * Grid A* used by the client for click-to-move. Returns tile-center waypoints, or null.
 * Bounded so a click across the map on a low-end device never stalls a frame.
 */
export function findPath(map: ZoneMap, sx: number, sy: number, gx: number, gy: number, maxNodes = 2500): { x: number; y: number }[] | null {
  const w = map.w;
  const h = map.h;
  const start = Math.floor(sy) * w + Math.floor(sx);
  let goalX = Math.floor(gx);
  let goalY = Math.floor(gy);
  if (tileAt(map, goalX, goalY) !== T_FLOOR) {
    const n = nearestWalkable(map, gx, gy, 6);
    goalX = Math.floor(n.x);
    goalY = Math.floor(n.y);
    if (tileAt(map, goalX, goalY) !== T_FLOOR) return null;
  }
  const goal = goalY * w + goalX;
  if (start === goal) return [{ x: gx, y: gy }];

  const g = new Map<number, number>();
  const parent = new Map<number, number>();
  const heapN: number[] = [];
  const heapF: number[] = [];
  const push = (n: number, f: number) => {
    heapN.push(n);
    heapF.push(f);
    let i = heapN.length - 1;
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (heapF[p] <= heapF[i]) break;
      [heapF[p], heapF[i]] = [heapF[i], heapF[p]];
      [heapN[p], heapN[i]] = [heapN[i], heapN[p]];
      i = p;
    }
  };
  const pop = (): number => {
    const top = heapN[0];
    const ln = heapN.pop()!;
    const lf = heapF.pop()!;
    if (heapN.length) {
      heapN[0] = ln;
      heapF[0] = lf;
      let i = 0;
      for (;;) {
        const l = i * 2 + 1;
        const r = l + 1;
        let m = i;
        if (l < heapN.length && heapF[l] < heapF[m]) m = l;
        if (r < heapN.length && heapF[r] < heapF[m]) m = r;
        if (m === i) break;
        [heapF[m], heapF[i]] = [heapF[i], heapF[m]];
        [heapN[m], heapN[i]] = [heapN[i], heapN[m]];
        i = m;
      }
    }
    return top;
  };
  const heur = (n: number) => {
    const nx = n % w;
    const ny = (n / w) | 0;
    const dx = Math.abs(nx - goalX);
    const dy = Math.abs(ny - goalY);
    return Math.max(dx, dy) + 0.41 * Math.min(dx, dy);
  };
  g.set(start, 0);
  push(start, heur(start));
  let nodes = 0;
  let best = start;
  let bestH = heur(start);
  const DIRS = [[1, 0, 1], [-1, 0, 1], [0, 1, 1], [0, -1, 1], [1, 1, 1.41], [1, -1, 1.41], [-1, 1, 1.41], [-1, -1, 1.41]];
  while (heapN.length && nodes < maxNodes) {
    const cur = pop();
    nodes++;
    if (cur === goal) {
      best = goal;
      break;
    }
    const cx = cur % w;
    const cy = (cur / w) | 0;
    const cg = g.get(cur)!;
    const curH = heur(cur);
    if (curH < bestH) {
      bestH = curH;
      best = cur;
    }
    for (const [dx, dy, cost] of DIRS) {
      const nx = cx + dx;
      const ny = cy + dy;
      if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
      if (map.tiles[ny * w + nx] !== T_FLOOR) continue;
      if (dx !== 0 && dy !== 0) {
        if (map.tiles[cy * w + nx] !== T_FLOOR || map.tiles[ny * w + cx] !== T_FLOOR) continue;
      }
      const n = ny * w + nx;
      const ng = cg + cost;
      const old = g.get(n);
      if (old === undefined || ng < old) {
        g.set(n, ng);
        parent.set(n, cur);
        push(n, ng + heur(n));
      }
    }
  }
  const out: { x: number; y: number }[] = [];
  let c = best;
  while (c !== start) {
    out.push({ x: (c % w) + 0.5, y: ((c / w) | 0) + 0.5 });
    const p = parent.get(c);
    if (p === undefined) break;
    c = p;
  }
  out.reverse();
  if (best === goal && out.length) {
    out[out.length - 1] = { x: goalX + 0.5, y: goalY + 0.5 };
  }
  return out.length ? out : null;
}
